// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import resetPdfSettings from "@bg/App/Handlers/PdfGenerator/ClearPdfSettings";
import generatePdf from "@bg/App/Handlers/PdfGenerator/GeneratePdf";
import updatePdfSettings from "@bg/App/Handlers/PdfGenerator/UpdatePdfSettings";
import uploadLogo from "@bg/App/Handlers/RequestQuote/UploadLogo";
import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { parseBoolean } from "@bg/XDL/Utilities/helpers";
import { formatPhone, validateEmail, validatePhone } from "@bg/XDL/Utilities/inputValidation";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import translate from "@insite/client-framework/Translate";
import Accordion from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps, CheckboxStyle } from "@insite/mobius/Checkbox";
import FileUpload, { FileUploadPresentationProps } from "@insite/mobius/FileUpload";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal from "@insite/mobius/Modal";
import Radio, { RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    id: string;
    showUploadLogo?: boolean;
    isQuote?: boolean;
    showIncludePrice?: boolean;
}

const mapStateToProps = (state: CustomAppState) => ({
    isSalesPerson: getSession(state).isSalesPerson,
    // get the images for each line on quote details page
    lineImages: state.commonActions["lineImages"],
    quoteId: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId).value?.id,
    quote: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId).value,
    logoName: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId).value?.properties["logoFileName"],
    quoteLines: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId).value?.quoteLineCollection,
    customSettings: (getSettingsCollection(state) as any)?.customSettings,
    salesRepInfo: getSession(state).properties["primarySalesPerson"],
    wishList: getWishListState(state, state.pages.myListDetails.wishListId)?.value,
    pdfSettings: state.pdfSettings.parameters,
});

const mapDispatchToProps = {
    generatePdf,
    uploadLogo,
    updatePdfSettings,
    resetPdfSettings,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

enum PrintFormat {
    LineFormat = "lineFormat",
    Individual = "individual",
}

export interface MyListDetailsPDFStyles {
    accodrionTitle?: AccordionSectionPresentationProps & { initialExpanded: boolean };
    panelWrapper?: GridContainerProps;
    copyWrapper?: GridItemProps;
    copyText?: TypographyPresentationProps;
    uploadLogo?: FileUploadPresentationProps;
    logoWrapper?: InjectableCss;
    informationFieldsWrapper?: GridItemProps;
    printOptions?: GridItemProps;
    printOptinsLabel?: TypographyPresentationProps;
    taxOptions?: GridItemProps;
    bottomSectionWrapper?: GridItemProps;
    formFields?: TextFieldPresentationProps;

    salesInputFieldsWrapper?: GridItemProps;
    printFormatWrapper?: InjectableCss;
    printFormatGroup?: RadioGroupProps;
    pdfActionsWrapper?: InjectableCss;
    buttonsWrapper?: InjectableCss;
    emailPdfButton?: ButtonPresentationProps;
    savePdfButton?: ButtonPresentationProps;
    viewPdfWrapper?: InjectableCss;
    viewPdfLink?: LinkPresentationProps;
    viewPdfText?: TypographyPresentationProps;
    notesTextArea?: TextAreaProps;
}

export const styles: MyListDetailsPDFStyles = {
    accodrionTitle: {
        initialExpanded: true,
        headerProps: {
            css: css`
                margin-top: 30px;
                button {
                    border: 0;
                    border-top: 2px solid ${getColor("common.ui3")};
                    background: transparent;
                    padding: 12px 0 !important;
                    &:focus {
                        border: 0;
                        border-top: 2px solid ${getColor("common.ui3")};
                    }
                    span {
                        font-size: 22px;
                        line-height: ${fontConfig.lineHeight.small};
                        letter-spacing: 0.22px;
                        font-weight: ${fontConfig.weight.semiBold};
                    }
                }
            `,
        },
        panelProps: {
            css: css`
                border: 0;
                padding: 20px 30px 20px;
                overflow: hidden;
                background: ${getColor("common.ui4")};
                ${CheckboxStyle} {
                    margin-bottom: 15px;
                    span[role="checkbox"] {
                        background: ${getColor("common.white")};
                    }
                }
                && input + label:before {
                    background: ${getColor("common.white")};
                }
                input:hover:checked:not(:disabled) + label::before {
                    background: ${getColor("common.white")};
                    border-color: ${getColor("common.white")};
                }

                @media (max-width: 481px) {
                    padding: 20px;
                }
            `,
        },
    },
    panelWrapper: {
        gap: 40,
    },
    copyWrapper: {
        width: 12,
        css: css`
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            margin-bottom: 20px;
            &&&&&.sales-copy {
                margin-bottom: 10px;
            }
        `,
    },
    copyText: {
        lineHeight: "20px",
        weight: fontConfig.weight.semiBold,
        css: css`
            display: block;
            @media (max-width: 600px) {
                width: 100%;
                margin-bottom: ${fontConfig.spacing.xSmallHeading};
            }
        `,
    },
    informationFieldsWrapper: {
        width: [12, 6, 6, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
    printOptions: {
        width: [12, 6, 6, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
    printOptinsLabel: {
        weight: fontConfig.weight.semiBold,
        css: css`
            margin-bottom: ${fontConfig.spacing.heading};
            display: block;
        `,
    },
    taxOptions: {
        width: [12, 6, 6, 4, 4],
        css: css`
            flex-direction: column;
            legend {
                font-weight: ${fontConfig.weight.semiBold};
            }
        `,
    },
    bottomSectionWrapper: {
        width: 12,
        css: css`
            justify-content: flex-end;
            position: relative;
            margin-top: 10px !important;
            &:before {
                content: " ";
                position: absolute;
                top: 0;
                width: calc(100% + 40px);
                left: -20px;
                height: 1px;
                background: ${getColor("common.ui2")};
            }
            @media (max-width: 480px) {
                flex-wrap: wrap;
            }
        `,
    },

    formFields: {
        cssOverrides: {
            formField: css`
                label {
                    font-weight: ${fontConfig.weight.semiBold};
                }
            `,
            inputSelect: css`
                background: ${getColor("common.white")} !important;
            `,
        },
    },

    logoWrapper: {
        css: css`
            width: 100%;
            padding-top: 22px;
            && > div[class*="UploadWrapper"] {
                flex-direction: row-reverse;
                background: ${getColor("common.white")};
                align-items: center;
                padding-left: 5px;
                position: relative;
            }
        `,
    },

    uploadLogo: {
        buttonProps: {
            variant: "secondary",
            css: css`
                margin: 0;
                padding: 0 20px;
                min-height: 32px;
                height: 32px;
                line-height: 29px;
                margin-right: 10px;
            `,
        },
        cssOverrides: {
            formField: css`
                && > span {
                    position: absolute;
                    top: -22px;
                    left: 4px;
                    font-weight: ${fontConfig.weight.semiBold};
                }
            `,
            formInputWrapper: css`
                margin: 0 !important;
                && > div[class*="FileUploadStyle"] {
                    padding: 0;
                    border: 0;
                    background: ${getColor("common.white")};
                }
            `,
            descriptionWrapper: css``,
            inputSelect: css``,
        },
    },
    emailPdfButton: {
        sizeVariant: "small",
        css: css`
            margin-left: 40px;
            padding: 0 12px;
            &&& svg {
                color: ${getColor("text")};
                transition: all 200ms ease-in-out;
            }

            &&:hover svg {
                color: ${getColor("common.white")};
            }

            @media (max-width: 481px) {
                width: 100%;
                margin: 0;
            }
        `,
        icon: {
            src: "Mail",
            position: "left",
        },
    },
    savePdfButton: {
        sizeVariant: "small",
        variant: "secondary",
        css: css`
            padding: 0 12px;
            &&& svg {
                color: ${getColor("common.white")};
                transition: all 200ms ease-in-out;
            }

            &&:hover svg {
                color: ${getColor("text")};
            }
            @media (max-width: 481px) {
                width: 100%;
                margin: 0;
                margin-bottom: 15px;
            }
        `,
        icon: {
            src: "SavePdf",
            position: "left",
        },
    },
    viewPdfWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
        `,
    },
    viewPdfText: {
        variant: "h6",
        css: css`
            display: block;
            width: 100%;
        `,
    },
    viewPdfLink: linkIconStyleGenerator("Pdf", "15px", 16),
    notesTextArea: {
        cssOverrides: {
            formField: css`
                label {
                    font-weight: ${fontConfig.weight.semiBold};
                }
            `,
            inputSelect: css`
                background: ${getColor("common.white")} !important;
                resize: none;
            `,
        },
    },
};

const PDFPrintSettings: React.FC<Props> = ({
    isSalesPerson,
    id,
    showUploadLogo,
    isQuote,
    generatePdf,
    toaster,
    quoteLines,
    lineImages,
    quoteId,
    customSettings,
    uploadLogo,
    logoName,
    salesRepInfo,
    wishList,
    updatePdfSettings,
    pdfSettings,
    quote,
    showIncludePrice,
    resetPdfSettings,
}) => {
    const [addCoverPage, setAddCoverPage] = useState(!!(pdfSettings["withCoverPage"] || !isSalesPerson));
    const [tableOfContents, setTableOfContents] = useState(!!(pdfSettings["withTableOfContent"] || !isSalesPerson));
    const [aboutUs, setAboutUs] = useState(!!(pdfSettings["withAboutUs"] || !isSalesPerson));
    const [specSheets, setSpecSheets] = useState(pdfSettings["withSpecSheet"] || false);
    const [includePrice, setIncludePrice] = useState(pdfSettings["includePrice"] || showIncludePrice);
    const [preparedFor, setPreparedFor] = useState(pdfSettings["preparedFor"] || "");
    const [consultant, setConsultant] = useState(pdfSettings["consultant"] || "");
    const [projectName, setProjectName] = useState(pdfSettings["projectName"] || "");
    const [phone, setPhone] = useState(pdfSettings["phone"] || "");
    const [phoneError, setPhoneError] = useState("");
    const [printFormat, setPrintFormat] = useState(
        pdfSettings["productsInlineFormat"] ? PrintFormat.LineFormat : PrintFormat.Individual,
    );
    const [customerEmail, setCustomerEmail] = useState(pdfSettings["customerEmailAddress"] || "");
    const [customerEmailError, setCustomerEmailError] = useState("");
    const [generalNotes, setGeneralNotes] = useState(pdfSettings["generalNotes"] || "");
    const [loading, setLoading] = useState(false);
    const fileUploadRef = useRef({ value: "" } as HTMLInputElement);
    const [file, setFile] = useState<any>(null);
    const [isPdfReady, setIsPdfReady] = useState(false);
    const [pdfUrl, setPdfUrl] = useState("");
    const [hasUpdatedLogo, setHasUpdatedLogo] = useState(false);

    useEffect(() => {
        return () => {
            resetPdfSettings({});
        };
    }, []);

    useEffect(() => {
        if (isSalesPerson) {
            const phone = JSON.parse(salesRepInfo || "")?.Phone;
            const email = JSON.parse(salesRepInfo || "")?.Email;
            const name = JSON.parse(salesRepInfo || "")?.Name || JSON.parse(salesRepInfo || "")?.Email;
            const number = formatPhone(phone);
            setPhone(number);
            setConsultant(email);
            !isQuote && wishList && setPreparedFor(name);
        }
        if (!isQuote && wishList) {
            setProjectName(wishList.name);
        } else if (isQuote && quote) {
            setPreparedFor(quote.userLabel || quote.userName);
            setProjectName(quote.quoteNumber);
        } else {
            setProjectName("");
        }
    }, [isSalesPerson, salesRepInfo, wishList]);

    useEffect(() => {
        // on each change for quote & wishlist we need to remove the old url to avoid caching issue
        setPdfUrl("");
    }, [wishList, quote]);

    useEffect(() => {
        setPdfUrl("");
        updatePdfSettings({
            parameters: {
                withCoverPage: !isSalesPerson ? true : addCoverPage,
                withTableOfContent: !isSalesPerson ? true : tableOfContents,
                withAboutUs: !isSalesPerson ? true : aboutUs,
                withSpecSheet: specSheets,
                productsInlineFormat: printFormat === PrintFormat.LineFormat,
                customerEmailAddress: customerEmail,
                preparedFor,
                consultant,
                projectName,
                phone,
                pdfType: isQuote ? "quote" : "wishlist",
                id,
                generalNotes,
                includePrice,
            },
        });
    }, [
        addCoverPage,
        tableOfContents,
        aboutUs,
        specSheets,
        preparedFor,
        consultant,
        projectName,
        phone,
        printFormat,
        customerEmail,
        isQuote,
        id,
        generalNotes,
        includePrice,
    ]);

    if (!customSettings) {
        return <></>;
    }

    const addCoverPageHandler: CheckboxProps["onChange"] = (_, value) => {
        setAddCoverPage(value);
    };

    const addTableOfContentsHandler: CheckboxProps["onChange"] = (_, value) => {
        setTableOfContents(value);
    };

    const addAboutUsHandler: CheckboxProps["onChange"] = (_, value) => {
        setAboutUs(value);
    };

    const addSpecSheetsHandler: CheckboxProps["onChange"] = (_, value) => {
        setSpecSheets(value);
    };

    const addIncludePriceHandler: CheckboxProps["onChange"] = (_, value) => {
        setIncludePrice(value);
    };

    const handlePreparedForChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPreparedFor(e.target.value);
    };

    const handleConsultantChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConsultant(e.target.value);
    };

    const handleProjectNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setProjectName(e.target.value);
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPhoneError("");
        const number = formatPhone(e.target.value);
        setPhone(number);
    };

    const validatePhoneOnBlur = (e: any) => {
        if (e.currentTarget.value) {
            const error = validatePhone(e.currentTarget.value);
            if (error) {
                setPhoneError(error as string);
            } else {
                setPhoneError("");
            }
        }
    };

    const handleGeneralNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setGeneralNotes(e.currentTarget.value);
    };

    const handlePrintFormatSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as PrintFormat;
        setPrintFormat(value);
    };

    const handleCustomerEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCustomerEmailError("");
        setCustomerEmail(e.target.value);
    };

    const handleCustomerEmailBlur = (e: any) => {
        if (e.currentTarget.value) {
            const error = validateEmail(e.target.value);
            if (error) {
                setCustomerEmailError(error as string);
            } else {
                setCustomerEmailError("");
            }
        }
    };

    const buildFormDataForTax = (uploadedFile: any) => {
        const formData = new FormData();
        formData.append(`file`, uploadedFile, uploadedFile.name || "");

        formData.append(`quoteId`, quoteId!);

        return formData;
    };

    const fileChangeHandler = (event: React.ChangeEvent<any>) => {
        const uploadedFile = event.target.files[0];
        setFile(uploadedFile);
        if (uploadedFile) {
            const formData = buildFormDataForTax(uploadedFile);
            setLoading(true);
            uploadLogo({
                formData,
                quoteId: quoteId!,
                onError: error => {
                    setLoading(false);
                    toaster.addToast({ body: error, messageType: "danger" });
                },
                onSuccess: result => {
                    setHasUpdatedLogo(true);
                    setLoading(false);
                    toaster.addToast({ body: translate("Logo Uploaded"), messageType: "success" });
                },
            });
        }
    };

    const getQuoteLinesImagesWithId = () => {
        const productsImages = {} as any;
        if (isQuote && quoteLines) {
            quoteLines.forEach(quoteLine => {
                if (lineImages?.hasOwnProperty(quoteLine.id)) {
                    productsImages[quoteLine.erpNumber] = "";
                    productsImages[quoteLine.erpNumber] = lineImages[quoteLine.id].id;
                } else if (quoteLine.properties && quoteLine.properties.images) {
                    const lineImage = JSON.parse(quoteLine.properties.images)[0];
                    productsImages[quoteLine.erpNumber] = "";
                    productsImages[quoteLine.erpNumber] = lineImage.id;
                }
            });
        }
        return productsImages;
    };

    const onEmail = () => {
        if ((customerEmailError && customerEmail) || (phone && phoneError)) {
            return;
        }
        if (!customerEmail) {
            setCustomerEmailError("Email is required");
            return;
        }

        setLoading(true);
        const productsImages = getQuoteLinesImagesWithId();
        generatePdf({
            id,
            sendPdfByEmail: true,
            productsImages,
            includePrice: showIncludePrice && includePrice,
            onError: error => {
                if (error) {
                    setLoading(false);
                }
                if ("errorMessage" in error) {
                    toaster.addToast({ body: error.errorMessage, messageType: "danger" });
                } else {
                    toaster.addToast({ body: error, messageType: "danger" });
                }
            },

            onComplete: (result: any) => {
                if (result.successful) {
                    setLoading(false);
                    setHasUpdatedLogo(false);
                    toaster.addToast({ body: siteMessage("Custom_PDFEmailWillBeSent"), messageType: "success" });
                }
            },
        });
    };

    const onSave = () => {
        if (phone && phoneError) {
            return;
        }
        if (checkIfSettingsHasNotChanged()) {
            setIsPdfReady(true);
            return;
        }

        setLoading(true);
        const productsImages = getQuoteLinesImagesWithId();
        generatePdf({
            id,
            sendPdfByEmail: false,
            productsImages,
            includePrice: showIncludePrice && includePrice,
            onError: error => {
                if (error) {
                    setLoading(false);
                }
                if ("errorMessage" in error) {
                    toaster.addToast({ body: error.errorMessage, messageType: "danger" });
                } else {
                    toaster.addToast({ body: error, messageType: "danger" });
                }
            },
            onComplete: (result: any) => {
                if (result.successful) {
                    setLoading(false);
                }
                if (result.successful && result.result) {
                    const pdfLink = JSON.parse(result.result).pdfFileUrl;
                    if (pdfLink) {
                        toaster.addToast({ body: translate("PDF has been generated"), messageType: "success" });
                        setPdfUrl(pdfLink);
                        setIsPdfReady(true);
                        setHasUpdatedLogo(false);
                    } else {
                        toaster.addToast({
                            body: translate("An error occurred while generating the PDF"),
                            messageType: "danger",
                        });
                    }
                }
            },
        });
    };

    const checkIfSettingsHasNotChanged = () => {
        if (
            pdfUrl &&
            addCoverPage === pdfSettings["withCoverPage"] &&
            tableOfContents === pdfSettings["withTableOfContent"] &&
            specSheets === pdfSettings["withSpecSheet"] &&
            (printFormat === PrintFormat.LineFormat) === pdfSettings["productsInlineFormat"] &&
            aboutUs === pdfSettings["withAboutUs"] &&
            preparedFor === pdfSettings["preparedFor"] &&
            consultant === pdfSettings["consultant"] &&
            projectName === pdfSettings["projectName"] &&
            phone === pdfSettings["phone"] &&
            customerEmail === pdfSettings["customerEmailAddress"] &&
            !hasUpdatedLogo &&
            (isQuote ? quoteId === pdfSettings["id"] : wishList?.id === pdfSettings["id"]) &&
            includePrice === pdfSettings["includePrice"] &&
            generalNotes === pdfSettings["generalNotes"]
        ) {
            return true;
        }
        return false;
    };

    if (isQuote && parseBoolean(quote?.properties.isExpired)) {
        return null;
    }

    return (
        <Accordion
            headingLevel={1}
            css={css`
                @media print {
                    display: none;
                }
            `}
        >
            <ManagedAccordionSection {...styles.accodrionTitle} title={translate("PDF Display Settings")}>
                <LoadingOverlay loading={loading}>
                    <GridContainer {...styles.panelWrapper}>
                        <GridItem {...styles.copyWrapper}>
                            <Typography {...styles.copyText}>{siteMessage("Custom_PrintPDFCopy")}</Typography>
                        </GridItem>
                        <GridItem {...styles.informationFieldsWrapper}>
                            <GridContainer gap={20}>
                                {!isQuote && (
                                    <GridItem width={12}>
                                        <TextField
                                            {...styles.formFields}
                                            type="text"
                                            value={customerEmail}
                                            onChange={handleCustomerEmailChange}
                                            onBlur={handleCustomerEmailBlur}
                                            error={customerEmailError}
                                            maxLength={120}
                                            label={
                                                isSalesPerson
                                                    ? translate("Customer’s Email Address")
                                                    : translate("Email Address")
                                            }
                                            placeholder={
                                                isSalesPerson
                                                    ? translate("customer@emailaddress.com")
                                                    : translate("name@emailaddress.com")
                                            }
                                        />
                                    </GridItem>
                                )}
                                {isSalesPerson && (
                                    <GridItem width={12}>
                                        <TextField
                                            {...styles.formFields}
                                            type="text"
                                            value={preparedFor}
                                            maxLength={50}
                                            onChange={handlePreparedForChange}
                                            label={translate("Prepared for")}
                                            placeholder={translate("Type a Name")}
                                        />
                                    </GridItem>
                                )}
                                {isSalesPerson && (
                                    <GridItem width={12}>
                                        <TextField
                                            {...styles.formFields}
                                            type="text"
                                            value={consultant}
                                            maxLength={50}
                                            onChange={handleConsultantChange}
                                            label={translate("Consultant")}
                                            placeholder={translate("Type a Name")}
                                        />
                                    </GridItem>
                                )}
                                {isSalesPerson && (
                                    <GridItem width={12}>
                                        <TextField
                                            {...styles.formFields}
                                            type="text"
                                            value={projectName}
                                            maxLength={50}
                                            onChange={handleProjectNameChange}
                                            label={translate("Project Name")}
                                            placeholder={translate("Type a Name")}
                                        />
                                    </GridItem>
                                )}

                                {isSalesPerson && (
                                    <GridItem width={12}>
                                        <TextField
                                            {...styles.formFields}
                                            type="text"
                                            value={phone}
                                            maxLength={12}
                                            error={phoneError}
                                            onChange={handlePhoneChange}
                                            onBlur={validatePhoneOnBlur}
                                            label={translate("Phone")}
                                            placeholder={translate("xxx-xxx-xxxx")}
                                        />
                                    </GridItem>
                                )}

                                <GridItem width={12}>
                                    <TextArea
                                        {...styles.notesTextArea}
                                        value={generalNotes}
                                        maxLength={600}
                                        onChange={handleGeneralNotesChange}
                                        label={translate("Notes")}
                                        placeholder={translate("Type a Note")}
                                        rows={2}
                                    />
                                </GridItem>
                            </GridContainer>
                        </GridItem>

                        <GridItem {...styles.taxOptions}>
                            <Typography {...styles.printOptinsLabel}>{translate("PDF Page Options")}</Typography>
                            {isSalesPerson && (
                                <Checkbox
                                    checked={addCoverPage}
                                    onChange={addCoverPageHandler}
                                    data-test-selector="addCoverPage"
                                >
                                    {translate("Cover Page")}
                                </Checkbox>
                            )}
                            {isSalesPerson && (
                                <Checkbox
                                    checked={tableOfContents}
                                    onChange={addTableOfContentsHandler}
                                    data-test-selector="addTableOfContents"
                                >
                                    {translate("Table of Contents")}
                                </Checkbox>
                            )}
                            {/* {isSalesPerson && (
                                <Checkbox
                                    checked={aboutUs}
                                    onChange={addAboutUsHandler}
                                    data-test-selector="addAboutUs"
                                >
                                    {translate("About Us")}
                                </Checkbox>
                            )} */}
                            <Checkbox
                                checked={specSheets}
                                onChange={addSpecSheetsHandler}
                                data-test-selector="addSpecSheets"
                            >
                                {translate("Spec Sheets")}
                            </Checkbox>
                            {showIncludePrice && (
                                <Checkbox
                                    checked={includePrice}
                                    onChange={addIncludePriceHandler}
                                    data-test-selector="addIncludePrice"
                                >
                                    {translate("Include Price")}
                                </Checkbox>
                            )}
                            <RadioGroup
                                label={translate("Which Options Would You Like to Include In the .PDF?")}
                                {...styles.printFormatGroup}
                                value={printFormat}
                                onChangeHandler={handlePrintFormatSelect}
                            >
                                <Radio value="lineFormat">{translate("Products in line item format")}</Radio>
                                <Radio value="individual">{translate("Products on individual pages")}</Radio>
                            </RadioGroup>
                        </GridItem>

                        {showUploadLogo && (
                            <GridItem {...styles.printOptions}>
                                <StyledWrapper {...styles.logoWrapper}>
                                    <FileUpload
                                        {...styles.uploadLogo}
                                        inputRef={fileUploadRef}
                                        fileName={file?.name || logoName}
                                        placeholder={translate("Upload .JPG, .PNG, .BMP and .GIF")}
                                        accept={customSettings.allowedImageExtensions}
                                        buttonText={translate("Upload")}
                                        label={translate("Upload Logo")}
                                        labelPosition="top"
                                        onFileChange={fileChangeHandler}
                                        uid="uploadLogoInput"
                                    />
                                </StyledWrapper>
                            </GridItem>
                        )}
                        <GridItem {...styles.bottomSectionWrapper}>
                            <Button onClick={onSave} {...styles.savePdfButton}>
                                {translate("Save PDF")}
                            </Button>
                            {!isQuote && (
                                <Button onClick={onEmail} {...styles.emailPdfButton}>
                                    {translate("Email PDF")}
                                </Button>
                            )}
                        </GridItem>
                    </GridContainer>
                    <Modal
                        size={500}
                        headline={translate("Your PDF is ready")}
                        isOpen={isPdfReady}
                        handleClose={() => setIsPdfReady(false)}
                    >
                        <StyledWrapper {...styles.viewPdfWrapper}>
                            <Typography {...styles.viewPdfText}>
                                {translate("Your PDF generation is done, click the link below:")}
                            </Typography>
                            <Link target="_blank" href={pdfUrl} {...styles.viewPdfLink}>
                                {translate("View PDF")}
                            </Link>
                        </StyledWrapper>
                    </Modal>
                </LoadingOverlay>
            </ManagedAccordionSection>
        </Accordion>
    );
};

export default withToaster(connect(mapStateToProps, mapDispatchToProps)(PDFPrintSettings));
